import { useCallback, useEffect, useRef, useState } from 'react';

import { message } from 'antd';
import { Check, CircleAlert, History, LoaderCircle, Presentation, Ticket } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import usePayOrder, { queryPaid } from '../../components/PayQRCodes/usePayOrder';
import { rememberOrder } from '../writing/api';
import { downloadExport, pptApi } from './api';
import ConfirmModal from './components/ConfirmModal';
import ContentStep from './components/ContentStep';
import HistoryDrawer from './components/HistoryDrawer';
import PayModal from './components/PayModal';
import ResultStep from './components/ResultStep';
import TemplateStep from './components/TemplateStep';
import {
    DEFAULT_SETTINGS,
    PLAIN_TEXT_MAX,
    SCENARIOS,
    STATUS,
    UPLOAD_EXTENSIONS,
    UPLOAD_MAX_MB,
    WORKFLOW_STEPS,
} from './constants';
import {
    belongsToResult,
    buildSubmitPayload,
    countChars,
    errorCodeOf,
    fileExtension,
    forgetRedeemCode,
    formatPrice,
    isQueued,
    materialReady,
    needsPayment,
    newRequestId,
    rememberRedeemCode,
    sanitizeFileName,
} from './helpers';
import { usePoll } from './usePoll';

import styles from './index.module.scss';

const EMPTY_REQUEST = { id: null, fingerprint: null };

function contentHint(mode, textReady, promptChars, fileTask) {
    if (mode === 'text') {
        if (textReady) return '内容越具体，生成的页面越贴合；资料里没有的数据不会被编造。';
        if (promptChars > PLAIN_TEXT_MAX) return '正文超过 2 万字，请删减，或改为上传文档。';
        return '请先输入论文题目、研究内容或正文。';
    }
    if (!fileTask) return '请先上传论文或资料文档。';
    if (fileTask.status === STATUS.MATERIAL_PROCESSING) return '资料整理中，可以先去选择模板。';
    if (fileTask.status === STATUS.MATERIAL_FAILED) return '资料整理失败，请重新整理或换一个文件。';
    return '资料已整理好，下一步选择模板。';
}

const settingsFromOptions = (options = {}) => ({
    scenario: options.scenario || DEFAULT_SETTINGS.scenario,
    audience: options.audience || '',
    durationMinutes: options.durationMinutes ?? null,
    pageCount: options.pageCount ?? null,
    generateNotes: Boolean(options.generateNotes),
});

export default function PptStudio() {
    const [searchParams, setSearchParams] = useSearchParams();
    const taskParam = searchParams.get('task');

    const [step, setStep] = useState(0);
    const [mode, setMode] = useState('text');
    const [prompt, setPrompt] = useState(() =>
        (searchParams.get('content') || '').slice(0, PLAIN_TEXT_MAX),
    );
    const [requirement, setRequirement] = useState('');
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [fileTask, setFileTask] = useState(null);
    const [uploading, setUploading] = useState(null);
    const [savingMaterial, setSavingMaterial] = useState(false);
    const [template, setTemplate] = useState(null);
    const [task, setTask] = useState(null);
    const [ownedUid, setOwnedUid] = useState(null);
    const [loadingTask, setLoadingTask] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmHint, setConfirmHint] = useState('');
    const [quote, setQuote] = useState({ loading: true, data: null });
    const [submitting, setSubmitting] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [downloading, setDownloading] = useState(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [serviceStatus, setServiceStatus] = useState(null);
    // 写作订单带来的答辩PPT兑换码（/ppt?code=MLP...），付款弹窗里预填
    const [redeemPrefill, setRedeemPrefill] = useState(() =>
        rememberRedeemCode(searchParams.get('code')),
    );
    const [payOpen, setPayOpen] = useState(false);
    const [payTarget, setPayTarget] = useState(null);
    const [checkingPaid, setCheckingPaid] = useState(false);

    const requestRef = useRef(EMPTY_REQUEST);
    const headingRef = useRef(null);
    const topRef = useRef(null);
    const stepMounted = useRef(false);
    const paidNoticeRef = useRef('');
    const taskUidRef = useRef(null);
    taskUidRef.current = task?.uid || null;
    const taskTitleRef = useRef('');
    taskTitleRef.current = task?.title || task?.fileName || '';

    /** 记到首页「我的生成记录」：PPT 任务按 uid 回到本页（/ppt?task=） */
    const rememberPptOrder = (view, status, orderNo) => {
        if (!view?.uid) return;
        rememberOrder({
            no: orderNo || view.payment?.orderNo || view.uid,
            uid: view.uid,
            title: view.title || view.fileName || '答辩PPT',
            kind: 'PPT',
            status,
        });
    };

    const payOrder = usePayOrder({ onPaid: (orderNo) => handlePaid(orderNo) });
    const { stop: stopPayOrder } = payOrder;

    const priceText = formatPrice(quote.data?.price);

    const loadQuote = useCallback(async () => {
        setQuote((current) => ({ loading: true, data: current.data }));
        try {
            const data = await pptApi.quote();
            setQuote({ loading: false, data });
        } catch {
            setQuote((current) => ({ loading: false, data: current.data }));
        }
    }, []);

    useEffect(() => {
        let active = true;
        pptApi
            .status()
            .then((data) => {
                if (active) setServiceStatus(data);
            })
            .catch(() => {});
        loadQuote();
        return () => {
            active = false;
        };
    }, [loadQuote]);

    const restoreTemplate = useCallback((view) => {
        const templateId = view.options?.templateId;
        if (!templateId) return;
        setTemplate((current) =>
            current?.id === templateId
                ? current
                : { id: templateId, name: view.options.templateName || templateId },
        );
    }, []);

    /** 文档任务回到第一步的资料页（整理中 / 失败 / 已整理，或待支付时回来改选项） */
    const showMaterial = useCallback(
        (view) => {
            const options = view.options || {};
            setTask(null);
            setMode('file');
            setFileTask(view);
            setRequirement(options.requirement || '');
            setSettings(settingsFromOptions(options));
            setStep(0);
            restoreTemplate(view);
        },
        [restoreTemplate],
    );

    const applyLoadedTask = useCallback(
        (view) => {
            if (belongsToResult(view)) {
                setTask(view);
                setFileTask(null);
                setStep(2);
                restoreTemplate(view);
            } else {
                showMaterial(view);
            }
        },
        [restoreTemplate, showMaterial],
    );

    const closePay = useCallback(() => {
        setPayOpen(false);
        stopPayOrder();
    }, [stopPayOrder]);

    const openPay = (view) => {
        const payment = view?.payment;
        if (!payment?.orderNo || payment.paid) return;
        setPayTarget({ orderNo: payment.orderNo, price: payment.price ?? quote.data?.price });
        setPayOpen(true);
        payOrder.start(payment.orderNo);
    };

    // 同一个订单的到账提示只弹一次（付款轮询、任务轮询、兑换都可能先发现）
    const notifyPaid = useCallback((orderNo, text = '支付成功，开始生成答辩PPT') => {
        // 付款 / 兑换 / 轮询发现已付：首页记录标成已付款（合并同一任务的记录）
        if (taskUidRef.current) {
            rememberOrder({
                no: orderNo || taskUidRef.current,
                uid: taskUidRef.current,
                title: taskTitleRef.current || '答辩PPT',
                kind: 'PPT',
                status: 'PAID',
            });
        }
        if (orderNo && paidNoticeRef.current === orderNo) return;
        paidNoticeRef.current = orderNo || '';
        message.success(text);
    }, []);

    // 从地址栏 ?task= 恢复（刷新页面、从“我的 PPT”进入）
    useEffect(() => {
        if (!taskParam) return undefined;
        if (task?.uid === taskParam || fileTask?.uid === taskParam) return undefined;
        let active = true;
        setLoadingTask(true);
        pptApi
            .task(taskParam)
            .then((view) => {
                if (active) applyLoadedTask(view);
            })
            .catch(() => {
                if (active) setSearchParams({}, { replace: true });
            })
            .finally(() => {
                if (active) setLoadingTask(false);
            });
        return () => {
            active = false;
        };
    }, [taskParam, task?.uid, fileTask?.uid, applyLoadedTask, setSearchParams]);

    // 资料整理中：轮询文档任务
    usePoll(
        async () => {
            const view = await pptApi.task(fileTask.uid, { quiet: true });
            setFileTask((current) => (current?.uid === view.uid ? view : current));
        },
        3000,
        fileTask?.status === STATUS.MATERIAL_PROCESSING,
    );

    const applyPolledView = useCallback(
        (view) => {
            if (!view || taskUidRef.current !== view.uid) return;
            if (!belongsToResult(view)) {
                // 资料整理失败、自动生成被取消或后台提交未成功：回到资料页
                closePay();
                applyLoadedTask(view);
                if (view.status === STATUS.MATERIAL_FAILED)
                    message.error('资料整理失败，没有开始生成，请重新整理或换一个文件');
                else if (view.status === STATUS.SUBMIT_FAILED)
                    message.warning(view.error?.message || '没有开始生成，请检查后重新提交');
                return;
            }
            setTask(view);
        },
        [applyLoadedTask, closePay],
    );

    const refreshTask = useCallback(async () => {
        const uid = taskUidRef.current;
        if (!uid) return null;
        try {
            const view = await pptApi.task(uid, { quiet: true });
            applyPolledView(view);
            return view;
        } catch {
            return null;
        }
    }, [applyPolledView]);

    // 生成中 / 待支付 / 排队自动生成：轮询任务；待支付且没开付款弹窗时放慢
    const taskActive =
        Boolean(task) &&
        (task.status === STATUS.GENERATING ||
            task.status === STATUS.SUBMITTING ||
            task.status === STATUS.AWAITING_PAYMENT ||
            isQueued(task));
    const pollDelay = task && needsPayment(task) && !payOpen ? 4000 : 2000;
    usePoll(refreshTask, pollDelay, taskActive);

    // 轮询发现已付款（扫码后关了弹窗、或在别处兑换）：关掉付款弹窗
    const taskPaid = Boolean(task?.payment?.paid);
    const taskOrderNo = task?.payment?.orderNo;
    useEffect(() => {
        if (!taskPaid || !payOpen) return;
        closePay();
        notifyPaid(taskOrderNo);
    }, [taskPaid, payOpen, taskOrderNo, closePay, notifyPaid]);

    // 切换步骤后回到顶部，并把焦点交给当前步骤标题
    useEffect(() => {
        if (!stepMounted.current) {
            stepMounted.current = true;
            return;
        }
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        headingRef.current?.focus({ preventScroll: true });
    }, [step]);

    const promptChars = countChars(prompt);
    const textReady = prompt.trim().length > 0 && promptChars <= PLAIN_TEXT_MAX;
    const contentReady =
        mode === 'text'
            ? textReady
            : Boolean(fileTask) && fileTask.status !== STATUS.MATERIAL_FAILED;
    const materialPending = mode === 'file' && fileTask?.status === STATUS.MATERIAL_PROCESSING;
    const generateReady =
        Boolean(template?.id) &&
        (mode === 'text' ? textReady : materialReady(fileTask) || materialPending);

    let generateHint;
    if (!template) generateHint = '请选择一个模板。';
    else if (materialPending)
        generateHint = '资料还在整理中，现在就可以确认并支付：整理完成后自动开始生成。';
    else if (mode === 'file' && !materialReady(fileTask))
        generateHint = '资料尚未就绪，请回到上一步处理。';
    else
        generateHint = `已选择「${template.name}」${
            priceText ? `，生成需支付 ${priceText}，可用答辩PPT兑换码抵扣` : ''
        }`;

    const updateSettings = (patch) => setSettings((current) => ({ ...current, ...patch }));

    const handleUpload = async (file) => {
        if (!UPLOAD_EXTENSIONS.includes(fileExtension(file.name))) {
            message.warning('仅支持 Word（.docx / .doc）、PDF 或 TXT 文件');
            return;
        }
        if (file.size > UPLOAD_MAX_MB * 1024 * 1024) {
            message.warning(`文件超过 ${UPLOAD_MAX_MB}MB，请压缩或删减后上传`);
            return;
        }
        setUploading({ name: file.name, percent: 0 });
        try {
            const view = await pptApi.uploadMaterial(
                file,
                {
                    scenario: settings.scenario,
                    audience: settings.audience.trim(),
                    requirement: requirement.trim(),
                },
                (percent) =>
                    setUploading((current) => (current ? { ...current, percent } : current)),
            );
            setFileTask(view);
            setSearchParams({ task: view.uid }, { replace: true });
        } catch {
            // 拦截器已提示原因
        } finally {
            setUploading(null);
        }
    };

    const handleRebuild = async () => {
        if (!fileTask) return;
        try {
            setFileTask(await pptApi.rebuildMaterial(fileTask.uid));
        } catch {
            // 拦截器已提示原因
        }
    };

    const handleReplaceFile = () => {
        setFileTask(null);
        setSearchParams({}, { replace: true });
    };

    const handleSaveMaterial = async (material) => {
        if (!fileTask) return;
        setSavingMaterial(true);
        try {
            setFileTask(await pptApi.saveMaterial(fileTask.uid, material));
        } catch {
            // 拦截器已提示原因，界面保留原资料
        } finally {
            setSavingMaterial(false);
        }
    };

    const goToTemplates = () => {
        if (contentReady) setStep(1);
    };

    const openConfirm = () => {
        if (!generateReady) return;
        setConfirmHint('');
        setConfirmOpen(true);
        loadQuote();
    };

    const handleConfirm = async () => {
        const payload = buildSubmitPayload({
            settings,
            templateId: template.id,
            prompt: mode === 'text' ? prompt : requirement,
        });
        setSubmitting(true);
        setConfirmHint('');
        try {
            let view;
            if (mode === 'text') {
                // 内容不变就沿用同一个请求编号：重复点击、超时重发、待支付时回来再提交都不会重复建任务
                const fingerprint = JSON.stringify(payload);
                if (requestRef.current.fingerprint !== fingerprint) {
                    requestRef.current = { id: newRequestId(), fingerprint };
                }
                view = await pptApi.createTextTask({
                    ...payload,
                    clientRequestId: requestRef.current.id,
                });
            } else {
                view = await pptApi.submitFileTask(fileTask.uid, payload);
            }
            const unpaid = needsPayment(view);
            if (!(mode === 'text' && unpaid)) requestRef.current = EMPTY_REQUEST;
            setConfirmOpen(false);
            setSearchParams({ task: view.uid }, { replace: true });
            if (!belongsToResult(view)) {
                showMaterial(view);
                message.warning(view.error?.message || '没有开始生成，请检查资料后重试');
                return;
            }
            setOwnedUid(view.uid);
            setTask(view);
            setFileTask(null);
            setStep(2);
            rememberPptOrder(view, unpaid ? 'UNPAID' : 'PAID');
            if (unpaid) openPay(view);
        } catch (error) {
            if (errorCodeOf(error) === 'upstream_unreachable' || !error?.response) {
                setConfirmHint(
                    '提交结果还未确认。可以再次点击“确认，去支付”，会沿用同一份请求，不会重复创建订单。',
                );
            }
        } finally {
            setSubmitting(false);
        }
    };

    function handlePaid(orderNo) {
        closePay();
        notifyPaid(orderNo);
        refreshTask();
    }

    const handleRedeemed = (code) => {
        if (code === redeemPrefill) {
            forgetRedeemCode();
            setRedeemPrefill('');
        }
        closePay();
        notifyPaid(payTarget?.orderNo, '兑换成功，开始生成答辩PPT');
        refreshTask();
    };

    const handleCheckPaid = async () => {
        if (!payTarget?.orderNo) return;
        setCheckingPaid(true);
        try {
            let paid = false;
            try {
                paid = await queryPaid(payTarget.orderNo);
            } catch {
                // 查询失败时再看任务状态
            }
            const view = await refreshTask();
            if (paid || view?.payment?.paid) handlePaid(payTarget.orderNo);
            else message.info('暂未查到支付结果。如已付款，通常几秒内到账，页面会自动更新');
        } finally {
            setCheckingPaid(false);
        }
    };

    const handleRetry = async () => {
        if (!task) return;
        setRetrying(true);
        try {
            const view = await pptApi.retry(task.uid);
            applyLoadedTask(view);
            if (needsPayment(view)) openPay(view);
        } catch {
            // 拦截器已提示原因
        } finally {
            setRetrying(false);
        }
    };

    const handleDownload = async (kind) => {
        const url = task?.exportUrls?.[kind];
        if (!url) return;
        setDownloading(kind);
        try {
            await downloadExport(url, `${sanitizeFileName(task.title)}.${kind}`);
        } catch (error) {
            message.error(error.message || '下载失败，请稍后重试');
        } finally {
            setDownloading(null);
        }
    };

    const handleRestart = () => {
        closePay();
        setTask(null);
        setFileTask(null);
        setConfirmHint('');
        requestRef.current = EMPTY_REQUEST;
        setSearchParams({}, { replace: true });
        setStep(0);
    };

    const handleBackToEdit = () => {
        if (!task) return;
        closePay();
        if (task.inputType === 'file') {
            showMaterial(task);
            return;
        }
        if (needsPayment(task)) {
            // 待支付时回来修改：保留请求编号，内容没改就会拿回同一个任务和订单
            setTask(null);
            setConfirmHint('');
            setSearchParams({}, { replace: true });
            setStep(0);
            return;
        }
        handleRestart();
    };

    const handleCancelAutoSubmit = async () => {
        if (!task) return;
        setCancelling(true);
        try {
            const view = await pptApi.cancelAutoSubmit(task.uid);
            if (belongsToResult(view)) {
                setTask(view);
                message.info('资料已整理完成并开始生成，无法取消');
            } else {
                closePay();
                applyLoadedTask(view);
                message.success('已取消自动生成，资料整理好后可以先检查再生成');
            }
        } catch {
            // 拦截器已提示原因
        } finally {
            setCancelling(false);
        }
    };

    const handleHistorySelect = (uid) => {
        setHistoryOpen(false);
        if (uid !== task?.uid && uid !== fileTask?.uid) {
            closePay();
            setSearchParams({ task: uid });
        }
    };

    const dismissRedeemCode = () => {
        forgetRedeemCode();
        setRedeemPrefill('');
    };

    const scenarioLabel = SCENARIOS.find((item) => item.value === settings.scenario)?.label;
    const confirmRows = [
        [
            '内容',
            mode === 'text'
                ? `${prompt.trim().slice(0, 40)}${promptChars > 40 ? '…' : ''}`
                : materialPending
                  ? `${fileTask?.fileName || ''}（资料整理中）`
                  : `${fileTask?.fileName || ''}（${fileTask?.material?.sections?.length || 0} 个章节）`,
        ],
        ['用途', scenarioLabel],
        ['模板', template?.name],
        ['页数', Number.isInteger(settings.pageCount) ? `${settings.pageCount} 页` : '自动规划'],
        ['演讲备注', settings.generateNotes ? '生成' : '不生成'],
    ];

    const canEdit = Boolean(task) && (task.inputType === 'file' || task.uid === ownedUid);
    const paySubtitle = task
        ? [task.title || task.fileName, task.options?.templateName].filter(Boolean).join(' · ')
        : '';

    return (
        <main className={styles.page}>
            <div className={styles.container} ref={topRef}>
                <header className={styles.hero} data-compact={step !== 0}>
                    <div className={styles.heroBadge}>
                        <Presentation size={13} aria-hidden />
                        AI 答辩PPT
                    </div>
                    <h1>
                        一键生成<span>可编辑答辩PPT</span>
                    </h1>
                    <p>
                        输入研究内容或上传论文 Word /
                        PDF，选好用途与模板，自动规划页数、撰写内容并排版，导出原生可编辑的 PPTX。
                    </p>
                    <div className={styles.heroMeta}>
                        <span>
                            单次生成 <strong>{priceText || '按当前定价'}</strong>
                        </span>
                        <i aria-hidden />
                        <span>支持答辩PPT兑换码</span>
                        <i aria-hidden />
                        <span className={styles.freeTag}>
                            <Check size={12} aria-hidden />
                            失败免费重试
                        </span>
                    </div>
                </header>

                <div className={styles.toolbar}>
                    <ol className={styles.steps} aria-label="PPT 生成步骤">
                        {WORKFLOW_STEPS.map((item, index) => {
                            const done = index < step;
                            const content = (
                                <>
                                    <span aria-hidden>
                                        {done ? <Check size={13} /> : index + 1}
                                    </span>
                                    <div>
                                        {item.label}
                                        <small>{item.hint}</small>
                                    </div>
                                </>
                            );
                            return (
                                <li
                                    key={item.label}
                                    className={
                                        index === step
                                            ? styles.stepActive
                                            : done
                                              ? styles.stepDone
                                              : ''
                                    }
                                    aria-current={index === step ? 'step' : undefined}
                                >
                                    {done && step !== 2 ? (
                                        <button type="button" onClick={() => setStep(index)}>
                                            {content}
                                        </button>
                                    ) : (
                                        content
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                    <div className={styles.toolbarActions}>
                        <button
                            type="button"
                            className={styles.ghostButton}
                            onClick={() => setHistoryOpen(true)}
                        >
                            <History size={15} aria-hidden />
                            我的 PPT
                        </button>
                    </div>
                </div>

                {redeemPrefill ? (
                    <div className={styles.notice} data-tone="success" role="status">
                        <Ticket size={16} aria-hidden />
                        <p>
                            已带入答辩PPT兑换码{' '}
                            <strong className={styles.codeText}>{redeemPrefill}</strong>
                            ，确认生成后在付款弹窗里点「兑换」即可抵扣。
                        </p>
                        <button
                            type="button"
                            className={styles.textButton}
                            onClick={dismissRedeemCode}
                        >
                            不使用
                        </button>
                    </div>
                ) : null}
                {serviceStatus && serviceStatus.available === false ? (
                    <div className={styles.notice} data-tone="warn" role="alert">
                        <CircleAlert size={16} aria-hidden />
                        <p>
                            PPT 服务暂时不可用
                            {serviceStatus.message ? `：${serviceStatus.message}` : ''}
                            ，请稍后再试。
                        </p>
                    </div>
                ) : null}
                {loadingTask ? (
                    <div className={styles.notice} role="status">
                        <LoaderCircle className={styles.spin} size={15} aria-hidden />
                        <p>正在打开这份 PPT…</p>
                    </div>
                ) : null}

                {step === 0 ? (
                    <ContentStep
                        headingRef={headingRef}
                        mode={mode}
                        onModeChange={setMode}
                        prompt={prompt}
                        onPromptChange={setPrompt}
                        requirement={requirement}
                        onRequirementChange={setRequirement}
                        settings={settings}
                        onSettingsChange={updateSettings}
                        fileTask={fileTask}
                        uploading={uploading}
                        savingMaterial={savingMaterial}
                        onUpload={handleUpload}
                        onRebuild={handleRebuild}
                        onReplaceFile={handleReplaceFile}
                        onSaveMaterial={handleSaveMaterial}
                        canNext={contentReady}
                        nextHint={contentHint(mode, textReady, promptChars, fileTask)}
                        onNext={goToTemplates}
                    />
                ) : null}
                {step === 1 ? (
                    <TemplateStep
                        headingRef={headingRef}
                        scenario={settings.scenario}
                        selectedId={template?.id}
                        onSelect={setTemplate}
                        onBack={() => setStep(0)}
                        canGenerate={generateReady}
                        generateHint={generateHint}
                        onGenerate={openConfirm}
                    />
                ) : null}
                {step === 2 && task ? (
                    <ResultStep
                        headingRef={headingRef}
                        task={task}
                        canEdit={canEdit}
                        retrying={retrying}
                        cancelling={cancelling}
                        downloading={downloading}
                        onPay={() => openPay(task)}
                        onRetry={handleRetry}
                        onRestart={handleRestart}
                        onBackToEdit={handleBackToEdit}
                        onCancelAutoSubmit={handleCancelAutoSubmit}
                        onDownload={handleDownload}
                    />
                ) : null}
            </div>

            <ConfirmModal
                open={confirmOpen}
                rows={confirmRows}
                quote={quote}
                queued={materialPending}
                hasCode={Boolean(redeemPrefill)}
                submitting={submitting}
                hint={confirmHint}
                onCancel={() => setConfirmOpen(false)}
                onConfirm={handleConfirm}
            />
            <PayModal
                open={payOpen}
                payment={payTarget}
                subtitle={paySubtitle}
                queued={isQueued(task)}
                channels={payOrder.channels}
                onRetryChannel={payOrder.retry}
                redeemPrefill={redeemPrefill}
                onRedeemed={handleRedeemed}
                checking={checkingPaid}
                onCheck={handleCheckPaid}
                onClose={closePay}
            />
            <HistoryDrawer
                open={historyOpen}
                currentUid={task?.uid || fileTask?.uid}
                onClose={() => setHistoryOpen(false)}
                onSelect={handleHistorySelect}
            />
        </main>
    );
}
